import { Controller, useFormContext } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tinct/web-components';
import { createConnectedInputHelpers } from '@tinct/web-form';
import {
  SAML_ATTRIBUTE_PRESETS,
  SamlAttributePreset,
  samlAttributePresetOf,
  SSO_CERTIFICATE_MAX_LENGTH,
  SSO_SAML_METADATA_XML_MAX_LENGTH,
  SsoSamlBinding,
  SsoSamlConnectionFormValues,
  SsoSamlSource,
} from '@tinct/core-api-models';
import { InfoIcon } from 'lucide-react';
import { toPemCertificate } from '../certificate-file';
import { FileOrPasteInput } from './file-or-paste-input';

const { ControlledTextInput, ControlledSelectInput, ControlledSwitchInput } =
  createConnectedInputHelpers<SsoSamlConnectionFormValues>();

const SOURCES: { value: SsoSamlSource; label: string; hint: string }[] = [
  {
    value: 'metadata_url',
    label: 'Metadata URL',
    hint: 'The quickest way: the entity ID, the sign-on URL and the certificates are read from it.',
  },
  {
    value: 'metadata_xml',
    label: 'Metadata document',
    hint: 'For a provider that only lets you download its metadata.',
  },
  {
    value: 'manual',
    label: 'By hand',
    hint: 'Enter the three values yourself.',
  },
];

const BINDING_OPTIONS = [
  { value: 'redirect', label: 'Redirect (default)' },
  { value: 'post', label: 'POST' },
];

const BINDING_LABELS: Record<SsoSamlBinding, string> = {
  redirect: 'Redirect',
  post: 'POST',
};

const PRESET_OPTIONS: { value: SamlAttributePreset; label: string }[] = [
  ...(
    Object.keys(SAML_ATTRIBUTE_PRESETS) as Exclude<
      SamlAttributePreset,
      'custom'
    >[]
  ).map((preset) => ({
    value: preset,
    label: SAML_ATTRIBUTE_PRESETS[preset].label,
  })),
  { value: 'custom', label: 'Custom' },
];

const decodeText = (bytes: Uint8Array) =>
  new TextDecoder().decode(bytes).trim();

/**
 * The SAML half of the identity-provider form. The source picker decides which fields are asked
 * for, because the API accepts exactly one of the three descriptions — and reads a metadata document
 * over anything else that was sent.
 */
export function SsoSamlFields() {
  const form = useFormContext<SsoSamlConnectionFormValues>();
  const source = form.watch('source');
  const attributeNames = {
    attributeEmail: form.watch('attributeEmail'),
    attributeGivenName: form.watch('attributeGivenName'),
    attributeFamilyName: form.watch('attributeFamilyName'),
  };
  const preset = samlAttributePresetOf(attributeNames);

  const applyPreset = (chosen: SamlAttributePreset) => {
    if (chosen === 'custom') return;
    const { values } = SAML_ATTRIBUTE_PRESETS[chosen];
    form.setValue('attributeEmail', values.attributeEmail, {
      shouldDirty: true,
    });
    form.setValue('attributeGivenName', values.attributeGivenName, {
      shouldDirty: true,
    });
    form.setValue('attributeFamilyName', values.attributeFamilyName, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <Label>Values from your identity provider</Label>
        <Controller
          control={form.control}
          name="source"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="gap-3"
            >
              {SOURCES.map((option) => (
                <div key={option.value} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={option.value}
                    id={`sso-saml-source-${option.value}`}
                    aria-label={option.label}
                    className="mt-1"
                  />
                  <div className="flex flex-col">
                    <Label
                      htmlFor={`sso-saml-source-${option.value}`}
                      className="font-normal"
                    >
                      {option.label}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {option.hint}
                    </span>
                  </div>
                </div>
              ))}
            </RadioGroup>
          )}
        />
      </div>

      {source === 'metadata_url' && (
        <ControlledTextInput
          name="idpMetadataUrl"
          label="Metadata URL"
          placeholder="https://acme.okta.com/app/exk1abcd/sso/saml/metadata"
          description="Read once when you save it, then watched: a change of certificates there is reported to you, never applied on its own."
        />
      )}

      {source === 'metadata_xml' && (
        <Controller
          control={form.control}
          name="idpMetadataXml"
          render={({ field, fieldState }) => (
            <FileOrPasteInput
              id="sso-saml-metadata-xml"
              label="Metadata document"
              accept=".xml,text/xml,application/xml"
              placeholder="<EntityDescriptor …>"
              description="Read once and not stored; what it says is saved on the connection."
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxLength={SSO_SAML_METADATA_XML_MAX_LENGTH}
              readBytes={decodeText}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {source === 'manual' && (
        <>
          <ControlledTextInput
            name="idpEntityId"
            label="Identity provider entity ID"
            placeholder="http://www.okta.com/exk1abcd"
            description="Also called issuer. Changing it makes everyone bind to the provider again at their next sign-in."
          />
          <ControlledTextInput
            name="idpSsoUrl"
            label="Sign-on URL"
            placeholder="https://acme.okta.com/app/acme/exk1abcd/sso/saml"
          />
          <Controller
            control={form.control}
            name="idpCertificate"
            render={({ field, fieldState }) => (
              <FileOrPasteInput
                id="sso-saml-certificate"
                label="Signing certificate"
                accept=".pem,.cer,.crt,application/x-x509-ca-cert,application/pkix-cert"
                placeholder="-----BEGIN CERTIFICATE-----"
                description="The certificate your provider signs its responses with. A binary .cer is converted here."
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                maxLength={SSO_CERTIFICATE_MAX_LENGTH}
                readBytes={toPemCertificate}
                error={fieldState.error?.message}
              />
            )}
          />
        </>
      )}

      {/* The binding is only ours to choose by hand: reading a metadata document sets it from what
          the document announces, so offering a control beside it would promise what it cannot keep. */}
      {source === 'manual' ? (
        <ControlledSelectInput
          name="idpSsoBinding"
          label="Sign-on binding"
          options={BINDING_OPTIONS}
          description="How the authentication request travels to your provider. Redirect suits every common provider."
          direction="horizontal"
        />
      ) : (
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-semibold">Sign-on binding</span>
          <span className="text-sm text-muted-foreground">
            {BINDING_LABELS[form.getValues('idpSsoBinding')]}, from your
            provider's metadata
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 border-t pt-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <Label
              htmlFor="sso-saml-attribute-preset"
              className="font-semibold"
            >
              Attribute names
            </Label>
            <span className="text-sm text-muted-foreground">
              Where the e-mail and the names are read in the assertion. Left
              empty, the names the common providers use are all tried.
            </span>
          </div>
          <Select
            value={preset}
            onValueChange={(value) => applyPreset(value as SamlAttributePreset)}
          >
            <SelectTrigger id="sso-saml-attribute-preset" className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  // "Custom" is what the fields below say, not something to pick.
                  disabled={option.value === 'custom'}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ControlledTextInput
          name="attributeEmail"
          label="E-mail"
          placeholder="email"
        />
        <ControlledTextInput
          name="attributeGivenName"
          label="First name"
          placeholder="firstName"
        />
        <ControlledTextInput
          name="attributeFamilyName"
          label="Last name"
          placeholder="lastName"
        />
      </div>

      <div className="flex flex-col gap-3 border-t pt-6">
        <ControlledSwitchInput
          name="allowIdpInitiated"
          label="Allow sign-in started from the identity provider"
          description="Off, a response must answer a request Tinct made. On, a tile in your provider's dashboard works — at the price of accepting responses nobody here asked for."
        />
      </div>

      <Alert>
        <InfoIcon />
        <AlertDescription>
          Changing the entity ID or the sign-on URL, typed here or read from
          metadata, sends the connection back to draft: single sign-on is
          switched off and the requirement lifted until a new test sign-in
          succeeds. Certificates are different: adding one keeps the status, so
          a rotation causes no outage, and only a change that leaves none of
          the previously valid certificates returns the connection to draft.
          The attribute names and the switch above never do.
        </AlertDescription>
      </Alert>
    </>
  );
}
